import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type { DecompositionNode } from 'api/data/projects/types';
import { normalizeId } from 'utils/ids';

import type { DecompositionTreePage, TreeCacheScope } from '../types';
import { mapMatchingNodesInPages, removeMatchingNodesInPages } from './nodeListMutations';
import { nodeMatchesId } from './treeCacheScope';
import { treeQueryKeys } from './treeQueryKeys';

/** Mutations on the paginated lists only — roots and each parent's children. */

type PagesCache = InfiniteData<DecompositionTreePage, number>;

function matcherFor(nodeId: string): (node: DecompositionNode) => boolean {
  const normalId = normalizeId(nodeId);
  return (node) => nodeMatchesId(node, normalId);
}

/**
 * Patches a node wherever it appears in the paginated lists. Used by `onDataNodeUpdated` to apply a
 * title change directly, with no refetch.
 */
export function patchNodeInPaginatedPages(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  nodeId: string,
  patch: Partial<DecompositionNode>
): void {
  const matches = matcherFor(nodeId);

  queryClient.setQueriesData<PagesCache>({ predicate: treeQueryKeys.pages(scope) }, (prev) =>
    mapMatchingNodesInPages(prev, matches, (node) => ({ ...node, ...patch }))
  );
}

/** Removes a node from one parent's children list only. */
export function removeNodeFromParentChildrenPages(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  parentDataNodeId: string,
  nodeId: string
): void {
  queryClient.setQueryData<PagesCache>(treeQueryKeys.children(scope, parentDataNodeId), (prev) =>
    removeMatchingNodesInPages(prev, matcherFor(nodeId))
  );
}

/** Removes a node from every paginated list — roots and all children. */
export function removeNodeFromPaginatedPages(queryClient: QueryClient, scope: TreeCacheScope, nodeId: string): void {
  const matches = matcherFor(nodeId);

  queryClient.setQueriesData<PagesCache>({ predicate: treeQueryKeys.pages(scope) }, (prev) =>
    removeMatchingNodesInPages(prev, matches)
  );
}
